import { graphql } from "./types";

export const StartChatQuery = graphql(`
  query StartChat($patientCase: String, $modelver: String) {
    startChat(patientCase: $patientCase, modelver: $modelver) {
      text
      context
    }
  }
`);

export const GetInitInfo = graphql(`
  query GetInitInfo {
    promptVariants
    wssEndpoint
  }
`);

export const getSessionsQuery = graphql(`
  query sessions($showOnlyActive: Boolean) {
    sessions(showOnlyActive: $showOnlyActive) {
      sessionId
      title
      active
      date
    }
  }
`);

export const getSessionsByUserQuery = graphql(`
  query sessionsByUser($showOnlyActive: Boolean, $username: String) {
    sessionsByUser(showOnlyActive: $showOnlyActive, username: $username) {
      sessionId
      title
      active
      date
    }
  }
`);

export const startSession = graphql(`
  mutation startSession(
    $patientCase: String
    $modelver: String
    $generateSuggestion: Boolean
  ) {
    startSession(
      patientCase: $patientCase
      modelver: $modelver
      generateSuggestion: $generateSuggestion
    ) {
      sessionId
      reply {
        context
        wsSessionId
        title
        valid
        sampleQuestions
        title
        valid
        debug
      }
    }
  }
`);

graphql(`
  fragment exchangeFragment on SessionExchange {
    index
    answer
    question
    citations {
      text
      rvids
      ruleCitations
    }
    feedback {
      positive
      freeText
      predefinedText
    }
    guidelines {
      ...guidelineFragment
    }
    altGuidelines {
      ...guidelineFragment
    }
  }
`);

graphql(`
  fragment sessionFragment on Session {
    context
    patientCase
    suggestedQuestions
    wsSessionId
    exchanges {
      ...exchangeFragment
    }
  }
`);

graphql(`
  fragment guidelineFragment on Guideline {
    id
    title
    url
    source
    properties {
      key
      value
    }
  }
`);

graphql(`
  fragment citationFragment on Citation {
    text
    rvids
    ruleCitations
  }
`);

export const restoreSession = graphql(`
  query restoreSession($sessionId: String) {
    restoreSession(sessionId: $sessionId) {
      ...sessionFragment
    }
  }
`);

export const restoreAnySession = graphql(`
  query restoreAnySession($sessionId: String) {
    restoreAnySession(sessionId: $sessionId) {
      ...sessionFragment
    }
  }
`);

export const startSessionByCaseIdNoCheck = graphql(`
  mutation StartSessionByCaseIdNoCheck(
    $patientCase: String
    $modelver: String
    $generateSuggestion: Boolean
    $caseId: String
  ) {
    startSessionByCaseIdNoCheck(
      patientCase: $patientCase
      modelver: $modelver
      generateSuggestion: $generateSuggestion
      caseId: $caseId
    ) {
      session {
        ...sessionFragment
      }
      sessionId
      valid
      history {
        ...exchangeFragment
      }
    }
  }
`);

export const startSessionByCaseIdWithCheck = graphql(`
  mutation StartSessionByCaseIdWithCheck(
    $patientCase: String
    $modelver: String
    $generateSuggestion: Boolean
    $caseId: String
  ) {
    startSessionByCaseIdWithCheck(
      patientCase: $patientCase
      modelver: $modelver
      generateSuggestion: $generateSuggestion
      caseId: $caseId
    ) {
      session {
        ...sessionFragment
      }
      sessionId
      valid
      history {
        ...exchangeFragment
      }
    }
  }
`);

export const answerQuestion = graphql(`
  query answerQuestion(
    $context: JSONObject
    $question: String
    $options: QuestionOptions
  ) {
    answerQuestion(context: $context, question: $question, options: $options) {
      text
      citations {
        ...citationFragment
      }
      guidelines {
        ...guidelineFragment
      }
      altGuidelines {
        ...guidelineFragment
      }
      context
      debug
    }
  }
`);

export const answerPrompt = graphql(`
  query answerPrompt(
    $context: JSONObject
    $options: QuestionOptions
    $predefinedInsight: PredefinedInsight
    $prompt: String
    $promptName: String
  ) {
    answerPrompt(
      context: $context
      options: $options
      predefinedInsight: $predefinedInsight
      prompt: $prompt
      promptName: $promptName
    ) {
      text
      citations {
        ...citationFragment
      }
      guidelines {
        ...guidelineFragment
      }
      altGuidelines {
        ...guidelineFragment
      }
      name
      debug
    }
  }
`);

export const askPromptInSession = graphql(`
  mutation askPromptInSession(
    $context: JSONObject
    $options: QuestionOptions
    $predefinedInsight: PredefinedInsight
    $prompt: String
    $promptName: String
    $sessionId: String
  ) {
    askPromptInSession(
      context: $context
      options: $options
      predefinedInsight: $predefinedInsight
      prompt: $prompt
      promptName: $promptName
      sessionId: $sessionId
    ) {
      text
      name
      debug
      citations {
        ...citationFragment
      }
      guidelines {
        ...guidelineFragment
      }
      altGuidelines {
        ...guidelineFragment
      }
    }
  }
`);

export const askQuestionInSession = graphql(`
  mutation askQuestionInSession(
    $context: JSONObject
    $question: String
    $sessionId: String
    $options: QuestionOptions
  ) {
    askQuestionInSession(
      context: $context
      question: $question
      sessionId: $sessionId
      options: $options
    ) {
      index
      citations {
        ...citationFragment
      }
      guidelines {
        ...guidelineFragment
      }
      altGuidelines {
        ...guidelineFragment
      }
      context
      text
      debug
    }
  }
`);

export const undo = graphql(`
  mutation undo($sessionId: String, $index: Int) {
    undo(sessionId: $sessionId, index: $index)
  }
`);

export const removeSession = graphql(`
  mutation removeSession($sessionId: String) {
    removeSession(sessionId: $sessionId) {
      status
    }
  }
`);

export const duplicateSession = graphql(`
  mutation DuplicateSession($sessionId: String, $takeover: Boolean) {
    duplicateSession(sessionId: $sessionId, takeover: $takeover) {
      sessionId
    }
  }
`);

export const updateExchangeFeedback = graphql(`
  mutation updateExchangeFeedback(
    $sessionId: String
    $index: Int
    $feedback: ExchangeFeedbackInput
  ) {
    updateExchangeFeedback(
      sessionId: $sessionId
      index: $index
      feedback: $feedback
    ) {
      feedback {
        positive
      }
    }
  }
`);

export const updatePromptFeedback = graphql(`
  mutation updatePromptFeedback(
    $sessionId: String
    $promptName: String
    $predefinedInsight: PredefinedInsight
    $feedback: ExchangeFeedbackInput
  ) {
    updatePromptFeedback(
      sessionId: $sessionId
      promptName: $promptName
      predefinedInsight: $predefinedInsight
      feedback: $feedback
    ) {
      feedback {
        positive
      }
    }
  }
`);

export const userTakeover = graphql(`
  mutation userTakeover($sessionId: String) {
    userTakeover(sessionId: $sessionId) {
      status
    }
  }
`);
